import React, { useEffect } from "react";

import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { menuTitleEnums } from "@enums";
import { StyleTimeSheet, TimeSheetCalendarComponent, TimeSheetToggleModal } from ".";
import {
  CircularLoader,
  TitleMenuComponent,
  AppCustomeSelect,
  AppExportCsv,
} from "..";
import { selectAuth, useAppSelector, selectGroupsLoading, selectGroupsData, selectGroupsError, selectEmployeesData, selectTimesheetsDataByMonth, fetchTimesheets, selectTimesheetsLoading } from "@redux";
import { checkExistUserSelected, linkImage, timesheetSelectableSpanOptions, useAppNavigation } from "@utils";
import { StyledGrayToggleButton } from "../WeeklySchedule/styles";
import { weeklyScheduleApi } from "src/Api/weekly-schedule";
import { useUserSelectionHistories } from "src/Utils/Hooks/useUserSelectionHistories";
import { useGroupSelectionHistories } from "src/Utils/Hooks/useGroupSelectionHistories";
import { useTimesheetSelectionHistories } from "src/Utils/Hooks/useTimesheetSelectionHistories";

const defaultGroup = {
  id: 0,
  name: "全て",
};

type SpanInput = {
  label: string;
  value: number;
} | null;

export const TimeSheetView = () => {
  // Redux store data
  const auth = useAppSelector(selectAuth);
  const id = auth?.userInfo?.id;
  const loading = useAppSelector(selectTimesheetsLoading);

  // hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const { navigateWithState, getQueryParam } = useAppNavigation();

  const currentDate = new Date();
  const initialYear = currentDate.getFullYear();
  const initialMonth = String(currentDate.getMonth() + 1).padStart(2, '0'); // Ensure 2 digits
  const initialCalendarMonth = (`${initialYear}-${initialMonth}`);
  const initialCsvStartDate = new Date(Date.UTC(initialYear, currentDate.getMonth(), 1));
  const initialCsvEndDate = new Date(Date.UTC(initialYear, currentDate.getMonth() + 1, 0));

  // URLクエリパラメータから取得（フィルター情報をURLで管理）
  const monthFromQuery = getQueryParam("month");
  const userIdFromQuery = getQueryParam("userId");
  const groupIdFromQuery = getQueryParam("groupId");
  const statusFromQuery = getQueryParam("status");
  const timesheetTypeFromQuery = getQueryParam("timesheetType");
  const fromQuery = getQueryParam("from");

  // component state
  const [timeSheetDateData, setTimeSheetDateData] = React.useState<any>();
  const [timeSheetIdData, setTimeSheetIdData] = React.useState<any>();
  const [timesheetListData, setTimeSheetListData] = React.useState<any>([]);
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [userInputValue, setUserInputValue] = React.useState<any>();
  const [groupInputValue, setGroupInputValue] = React.useState<any>();
  const [statusInputValue, setStatusInputValue] = React.useState<any>();
  const [spanInputValue, setSpanInputValue] = React.useState<SpanInput>(null);
  const [currentMonth, setCurrentMonth] = React.useState(initialCalendarMonth); // YYYY-MM format
  const [groups, setGroups] = React.useState<any>([]);
  const [selectedGroup, setSelectedGroup] = React.useState<any>([]);
  const [users, setUsers] = React.useState<any>([]);
  const [cvsStartDate, setCvsStartDate] = React.useState<string>(initialCsvStartDate.toISOString().split('T')[0]);
  const [cvsEndDate, setCvsEndDate] = React.useState<string>(initialCsvEndDate.toISOString().split('T')[0]);
  const [options, setOptions] = React.useState<{ label: string; value: string, type: string }[]>([]);
  const [spanOptions, setSpanOptions] = React.useState<{ label: string; value: number}[]>([]);
  const [timeSheetGroupIdData, setTimeSheetGroupIdData] = React.useState<any>();

  const [hasAutoSelected, setHasAutoSelected] = React.useState<boolean>(false);

  const statusOptions: { label: string; value: string }[] = [
    { label: "全て", value: "all" },
    { label: "未承認", value: "pending" },    // status 0
    { label: "承認済み", value: "approved" }, // status 1
    { label: "差し戻し", value: "rejected" }  // status 2
  ];

  // Redux selectors for groups, timesheets, and employees
  // const groupsLoading = useAppSelector(selectGroupsLoading);
  const groupsData = useAppSelector(selectGroupsData);
  // const groupsError = useAppSelector(selectGroupsError);
  const timesheetsDataForMonth = useAppSelector((state) => selectTimesheetsDataByMonth(state, currentMonth));
  const employeesData = useAppSelector(selectEmployeesData);

  //選択履歴保存とリスト取得
  const { list, fetchList, storeSelection } = useUserSelectionHistories(id ?? 0);
  const { groupList, fetchGroupList, storeGroupSelection } = useGroupSelectionHistories(id ?? 0);
  const { timesheetList, fetchTimesheetList, storeTimesheetSelection } = useTimesheetSelectionHistories(id ?? 0);

  useEffect(() => {
    if (groupsData) { setGroups(groupsData); }
  }, [groupsData]);

  useEffect(() => {
    if (employeesData) {
      setUsers(employeesData);
    }
  }, [employeesData]);

  // URLクエリパラメータが変更されたときにcurrentMonthを同期
  useEffect(() => {
    const currentMonthFromQuery = getQueryParam("month");
    if (currentMonthFromQuery && currentMonthFromQuery !== currentMonth) {
      setCurrentMonth(currentMonthFromQuery);
    } else if (!currentMonthFromQuery && currentMonth !== initialCalendarMonth) {
      // クエリパラメータがない場合は現在月に戻す
      setCurrentMonth(initialCalendarMonth);
    }
  }, [getQueryParam, currentMonth, initialCalendarMonth]);

  // Check if timesheets for the selected month exist
  React.useEffect(() => {
    if (timesheetsDataForMonth) {
      let filteredTimesheets = filterTimesheets(timesheetsDataForMonth);

      if (statusInputValue && statusInputValue.value) {
        filteredTimesheets = (filterTimesheetsStatus(filteredTimesheets, statusInputValue.value));
      }

      if (spanInputValue && spanInputValue.value !== null) {
        filteredTimesheets = (filterTimesheetsSpan(filteredTimesheets, spanInputValue.value));
      }

      setTimeSheetListData(filteredTimesheets);
    }
  }, [timesheetsDataForMonth, userInputValue?.value, groupInputValue?.value, statusInputValue?.value, selectedGroup, spanInputValue?.value]);

  React.useEffect(() => {
    if (!timesheetsDataForMonth) {
      dispatch(fetchTimesheets({ month: currentMonth }));
    }
  }, [currentMonth, timesheetsDataForMonth, userInputValue?.value, groupInputValue?.value, statusInputValue?.value, selectedGroup, spanInputValue?.value, dispatch]);

  //タイムシート種類の履歴順並び替え。履歴がない場合は規定の順番。
  useEffect(()=>{
    if (!timesheetList || timesheetList.length === 0) {
      setSpanOptions(timesheetSelectableSpanOptions);
      return;
    }

    const sortedTypes = timesheetList.map(type => type.type);

    const sortedOptions = [...timesheetSelectableSpanOptions].sort(
        (a, b) => {
          const indexA = sortedTypes.indexOf(Number(a.value)); 
          const indexB = sortedTypes.indexOf(Number(b.value));
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        }
    );

    setSpanOptions(sortedOptions);

  },[timesheetList]);

  //ユーザーとグループのリアルタイム履歴反映のリスト読み込み
    useEffect(() => {
      if (list && groupList) {
        
        const userOption = list.map((user) => ({
          label: `${user.last_name} ${user.first_name}`,
          value: user.id.toString(),
          type: user.type,
        }));
        
        const currentUserOption = userOption.find((user) => user.value === id?.toString());
  
        const userOptionWithoutCurrentUser = userOption.filter((user) => user.value !== id?.toString());
        
        const groupOption = groupList.map((group) => ({
          label: group.name,
          value: group.id.toString(),
          type: group.type,
        }));

        setOptions(
          [currentUserOption, ...userOptionWithoutCurrentUser, ...groupOption].filter((option) => option !== undefined)
        );

        // 初回アクセス時のみログインユーザー既存選択固定
        if (!hasAutoSelected && currentUserOption) {
          setUserInputValue(currentUserOption);
          setHasAutoSelected(true);
        }
  
      }
    }, [list, groupList]);

  // URLクエリパラメータからフィルター値を復元
  useEffect(() => {
    if (options.length === 0) return;
    
    // 最新のクエリパラメータを取得（location.searchが変更された場合に対応）
    const currentUserIdFromQuery = getQueryParam("userId");
    const currentGroupIdFromQuery = getQueryParam("groupId");
    const currentStatusFromQuery = getQueryParam("status");
    const currentFromQuery = getQueryParam("from");
    
    // ユーザーIDまたはグループIDから選択状態を復元
    if (currentUserIdFromQuery === 'all') {
      // userId=allの場合は全員表示（フィルターをクリア）
      if (userInputValue || groupInputValue) {
        setUserInputValue(null);
        setGroupInputValue(null);
        setSelectedGroup([]);
      }
    } else if (currentUserIdFromQuery) {
      const userOption = options.find(opt => opt.type === "user" && opt.value === currentUserIdFromQuery);
      if (userOption && userOption.value !== userInputValue?.value) {
        setUserInputValue(userOption);
        setGroupInputValue(null);
        setSelectedGroup([]);
      }
    } else if (currentGroupIdFromQuery) {
      const groupOption = options.find(opt => opt.type === "group" && opt.value === currentGroupIdFromQuery);
      if (groupOption && groupOption.value !== groupInputValue?.value) {
        setGroupInputValue(groupOption);
        setUserInputValue("");
        const groupId = parseInt(currentGroupIdFromQuery);
        if (groupId !== 0) {
          setSelectedGroup([groupId]);
        } else {
          setSelectedGroup([]);
        }
      }
    } else {
      // userIdもgroupIdもない場合はログインユーザーを自動選択
      // (from=sidemenuの場合、または詳細から戻ってきた場合など)
      const currentUserOption = options.find((opt) => opt.value === id?.toString() && opt.type === "user");
      
      if (currentUserOption && userInputValue?.value !== currentUserOption.value) {
        setUserInputValue(currentUserOption);
        setGroupInputValue(null);
        setSelectedGroup([]);
      }
    }
    
    // ステータスの選択状態を復元
    if (currentStatusFromQuery) {
      const statusOption = statusOptions.find(opt => opt.value === currentStatusFromQuery);
      if (statusOption) {
        setStatusInputValue((prev: any) => (prev?.value === statusOption.value ? prev : statusOption));
      }
    } else {
      setStatusInputValue((prev: any) => (prev ? null : prev));
    }
  }, [options, getQueryParam, id, userInputValue, groupInputValue]);

  // 日報種別コンボ用：クエリから選択値を復元
  useEffect(() => {
    const currentTimesheetTypeFromQuery = getQueryParam("timesheetType");

    if (currentTimesheetTypeFromQuery) {
      const typeId = Number(currentTimesheetTypeFromQuery);
      if (!Number.isNaN(typeId)) {
        const typeOption = spanOptions.find((opt) => Number(opt.value) === typeId);
        if (typeOption && typeOption.value !== spanInputValue?.value) {
          setSpanInputValue(typeOption);
        }
      }
    } else if (!currentTimesheetTypeFromQuery && spanInputValue) {
      setSpanInputValue(null);
    }
  }, [getQueryParam, spanOptions, spanInputValue]);

  // events
  const handleOpenModal = (date: any, id: number) => {
    setOpenModal(true);
    setTimeSheetDateData({
      date: date.startDate[0],
    });
    setTimeSheetIdData({
      id,
    });
  };
  const handleOpenModalId = (date: any, id: number, groupId: number) => {
    setOpenModal(true);
    setTimeSheetDateData({
      date: date.startDate,
    });
    setTimeSheetIdData({
      id,
    });
    setTimeSheetGroupIdData({ groupId });
  };

  const handleCloseToggle = () => {
    setOpenModal(false);
    setTimeSheetDateData([]);
    setTimeSheetIdData("");
    setTimeSheetGroupIdData("");
  };

  /**
   * 月選択の変更を処理する
   * @param event - 月選択の入力イベント
   */
  const handleMonthSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = event.target.value; // The value will be in 'YYYY-MM-DD' format
    const selectedMonth = selectedDate.slice(0, 7); // Extract the month part
    handleMonthChange(selectedMonth);
  };

  /**
   * 月の変更を処理する共通関数（カレンダーと月選択の両方から使用）
   * @param newMonth - 新しい月（YYYY-MM形式）
   */
  const handleMonthChange = (newMonth: string) => {
    setCurrentMonth(newMonth);
    
    // URLクエリパラメータを更新（履歴に追加）
    // 月以外のフィルターも保持する
    navigateWithState('/timesheet', {
      month: newMonth,
      ...(userIdFromQuery && { userId: userIdFromQuery }),
      ...(groupIdFromQuery && { groupId: groupIdFromQuery }),
      ...(statusFromQuery && { status: statusFromQuery }),
      ...(timesheetTypeFromQuery && { timesheetType: timesheetTypeFromQuery }),
    }, 'push');
  };

  // const handleSelectGroup = (id) => {
  //   setInputValue("");
  //   if (id === 0) {
  //     setSelectedGroup([]);
  //     setTimeSheetListData(timesheetsDataForMonth);
  //     return;
  //   }
  //   const newSelectedGroup = checkExistUserSelected(id, selectedGroup) ? selectedGroup.filter((e) => e !== id) : [...selectedGroup, id];
  //   setSelectedGroup(newSelectedGroup);
  //   setTimeSheetListData(filterTimesheets(timesheetsDataForMonth, newSelectedGroup));
  // };

  /**
   * 人・班コンボボックス変更時に呼ばれる関数。
   * ユーザーまたはグループの選択に応じて、タイムシートデータをフィルタリングする
   * 
   * @param {Object} value - 選択された値
   * @param {string} value.type - 選択タイプ（'user' または 'group'）
   * @param {string} value.value - 選択された値（ユーザーIDまたはグループID）
   * 
   * 処理内容:
   * 1. 値が空の場合: 全てのフィルターをリセット
   * 2. ユーザー選択の場合: グループ選択をリセットし、ユーザーでフィルタリング
   * 3. グループ選択の場合: ユーザー選択をリセットし、グループでフィルタリング
   *    - グループIDが0の場合: 全てのグループを表示
   *    - それ以外: 選択されたグループのみを表示
   * 
   * @example
   * // ユーザー選択の場合
   * handleUserOrGroupSelection({ type: 'user', value: '123' })
   * 
   * // グループ選択の場合
   * handleUserOrGroupSelection({ type: 'group', value: '456' })
   */
  const handleUserOrGroupSelection = (value) => {
    if (!value) {
      setUserInputValue(null);
      setGroupInputValue(null);
      setSelectedGroup([]);
      
      // URLクエリパラメータで全員表示を明示（userId=all）
      navigateWithState('/timesheet', {
        month: currentMonth,
        userId: 'all',
        ...(statusFromQuery && { status: statusFromQuery }),
        ...(timesheetTypeFromQuery && { timesheetType: timesheetTypeFromQuery }),
      }, 'push');
      return;
    }

    if (value.type === "user") {
      setSelectedGroup([]);
      setUserInputValue(value);
      weeklyScheduleApi.updateOrderListName(value.value);
      //選択したユーザーの履歴保存
      storeSelection({ user_id: id, selected_user_id: value.value });
      
      // URLクエリパラメータを更新（月とステータスは保持）
      navigateWithState('/timesheet', {
        month: currentMonth,
        userId: value.value,
        ...(statusFromQuery && { status: statusFromQuery }),
        ...(timesheetTypeFromQuery && { timesheetType: timesheetTypeFromQuery }),
      }, 'push');
    }

    if (value.type === "group") {
      setUserInputValue("");
      setGroupInputValue(value);
      setSelectedGroup([]);
      const groupId = parseInt(value.value);

      if (groupId === 0) {
        setSelectedGroup([]);
        setTimeSheetListData(timesheetsDataForMonth);
        
        // URLクエリパラメータを更新（月とステータスは保持）
        navigateWithState('/timesheet', {
          month: currentMonth,
          ...(statusFromQuery && { status: statusFromQuery }),
          ...(timesheetTypeFromQuery && { timesheetType: timesheetTypeFromQuery }),
        }, 'push');
        return;
      }

      //選択したグループの履歴保存
      storeGroupSelection({ user_id: id, selected_group_id: value.value });

      setSelectedGroup(() => {
        const updatedGroup = [groupId];
        setTimeSheetListData(filterTimesheets(timesheetsDataForMonth, updatedGroup));

        return updatedGroup;
      });
      
      // URLクエリパラメータを更新（月とステータスは保持）
      navigateWithState('/timesheet', {
        month: currentMonth,
        groupId: value.value,
        ...(statusFromQuery && { status: statusFromQuery }),
        ...(timesheetTypeFromQuery && { timesheetType: timesheetTypeFromQuery }),
      }, 'push');
    }
  };

  const handleOnChangeStatus = (value) => {
    if (!value) {
      setStatusInputValue(null);
      navigateWithState('/timesheet', {
        month: currentMonth,
        ...(userIdFromQuery && { userId: userIdFromQuery }),
        ...(groupIdFromQuery && { groupId: groupIdFromQuery }),
        ...(timesheetTypeFromQuery && { timesheetType: timesheetTypeFromQuery }),
      }, 'push');
      return;
    }

    setStatusInputValue(value);
    navigateWithState('/timesheet', {
      month: currentMonth,
      ...(userIdFromQuery && { userId: userIdFromQuery }),
      ...(groupIdFromQuery && { groupId: groupIdFromQuery }),
      ...(timesheetTypeFromQuery && { timesheetType: timesheetTypeFromQuery }),
      status: value.value,
    }, 'push');
  };

  const handleOnChangeSpan = (value) => {
    if (!value) {
      setSpanInputValue(null);
      navigateWithState('/timesheet', {
        month: currentMonth,
        ...(userIdFromQuery && { userId: userIdFromQuery }),
        ...(groupIdFromQuery && { groupId: groupIdFromQuery }),
        ...(statusFromQuery && { status: statusFromQuery }),
      }, 'push');
      return;
    }
    //選択した日報タイプの履歴保存
    storeTimesheetSelection({ user_id: id, selected_timesheet_type: Number(value.value) });

    setSpanInputValue(value);
    navigateWithState('/timesheet', {
      month: currentMonth,
      ...(userIdFromQuery && { userId: userIdFromQuery }),
      ...(groupIdFromQuery && { groupId: groupIdFromQuery }),
      ...(statusFromQuery && { status: statusFromQuery }),
      timesheetType: value.value,
    }, 'push');
  };

  const filterTimesheetsSpan = (timesheets, selectedSpan: number | null) => {
    if (selectedSpan === null || selectedSpan === undefined) return timesheets;
    return timesheets.map((day) => {
      const filteredUsers = day.users.filter((user) => {

        const userHasMatchingTimesheet = user.timesheets.some((timesheet) => {
          const isDateMatching = timesheet.date === day.startDate;

          let isTypeMatching = true;

          if (selectedSpan !== null || selectedSpan !== undefined) {
            isTypeMatching = selectedSpan === timesheet.type;
          }

          return isDateMatching && isTypeMatching;

        });

        return userHasMatchingTimesheet;
      });

      return {
        ...day,
        users: filteredUsers,
      };
    });
  };

  const filterTimesheetsStatus = (timesheets, selectedStatus) => {
    const status = selectedStatus === "approved" ? 1 : selectedStatus === "rejected" ? 2 : selectedStatus === "pending" ? 0 : 3;
    return timesheets.map((day) => {
      const filteredUsers = day.users.filter((user) => {

        const userHasMatchingTimesheet = user.timesheets.some((timesheet) => {
          const isDateMatching = timesheet.date === day.startDate;

          let isStatusMatching = true;

          if (status !== 3) {
            isStatusMatching = status === timesheet.status;
          }

          return isDateMatching && isStatusMatching;

        });

        return userHasMatchingTimesheet;
      });

      return {
        ...day,
        users: filteredUsers,
      };
    });
  };

  const filterTimesheets = (timesheets, mewSelectedGroup = selectedGroup) => {
    return timesheets.map((day) => {
      // Filter users for the current day
      const filteredUsers = day.users.filter((user) => {
        // Filter the user's timesheets based on date and group_id conditions
        const isUserMatching = !userInputValue || parseInt(userInputValue.value) === user.id;

        const userHasMatchingTimesheet = user.timesheets.some((timesheet) => {
          // Condition 1: The timesheet date must match the day's startDate
          const isDateMatching = timesheet.date === day.startDate;

          // Condition 2: If no groups are selected, all timesheets are valid.
          // Otherwise, only timesheets with a group_id in selectedGroup should be considered.
          const isGroupMatching =
            mewSelectedGroup.length === 0 || mewSelectedGroup.includes(timesheet.group_id);

          return isDateMatching && isGroupMatching;
        });

        // Include the user only if they have at least one matching timesheet
        return isUserMatching && userHasMatchingTimesheet;
      });

      // Return a new day object with filtered users. If no users match, this day will have an empty users array.
      return {
        ...day,
        users: filteredUsers,
      };
    });
  };

  // const groupsContent = groupsLoading ? (
  //   <span>Loading...</span>
  // ) : groupsError ? (
  //   <div> Groups error: {groupsError} </div>
  // ) : (
  //   groups.map((item) => (
  //     <StyledGrayToggleButton
  //       key={item.id}
  //       isActive={checkExistUserSelected(item.id, selectedGroup)}
  //       onClick={() => handleSelectGroup(item.id)}
  //     >
  //       {item.name}
  //     </StyledGrayToggleButton>
  //   ))
  // );

  return (
    <StyleTimeSheet>
      {/* <AppTopBar /> */}
      <TimeSheetToggleModal
        openToogle={openModal}
        anchor="right"
        handleCloseToggle={handleCloseToggle}
        timeSheetDateData={timeSheetDateData}
        // id={timeSheetIdData}
        id={userInputValue?.value ?? null}
        // selectedGroup={selectedGroup}
        groupId={groupInputValue?.value ?? null}
        status={statusInputValue?.value ?? null}
        type={Number(spanInputValue?.value) ?? null}
        span={spanInputValue?.value ?? null}
      />
      <CircularLoader loading={loading} />
      <div className="timesheet">
        <div className="timesheet__title to-link">
          <TitleMenuComponent text={menuTitleEnums.TITLE_TIMESHEET} />
        </div>

        <div className="timesheet__datePicker">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: "0.5rem" }}>
            <div className="label">月を選択</div>
            <div className="wrapperDatePicker">
              <input
                type="month"
                value={currentMonth}
                onChange={handleMonthSelection}
                className="inputMonth"
              />
            </div>
          </div>

          <div className="timesheet__search">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: "0.5rem" }}>
              <div className="label">日報種別</div>
              <div className="wrapperSelectGroup">
                <AppCustomeSelect
                  placeholder="種別を選択"
                  onChange={handleOnChangeSpan}
                  groupUserOptions={spanOptions}
                  options={spanOptions}
                  value={spanInputValue}
                  isClearable
                  isSearchable={false}
                />
              </div>
            </div>
          </div>

          <div className="timesheet__search">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: "0.5rem" }}>
              <div className="label">班・従業員</div>
              <div className="wrapperSelectGroup">
                <AppCustomeSelect
                  placeholder="人・班を選択"
                  onChange={handleUserOrGroupSelection}
                  groupUserOptions={options}
                  options={options}
                  value={(userInputValue === "") ? groupInputValue : userInputValue}
                  isClearable
                />
              </div>
            </div>
          </div>

          <div className="timesheet__search">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: "0.5rem" }}>
              <div className="label">ステータス</div>
              <div className="wrapperSelectGroup">
                <AppCustomeSelect
                  placeholder="ステータスを選択"
                  onChange={handleOnChangeStatus}
                  groupUserOptions={statusOptions}
                  options={statusOptions}
                  value={statusInputValue}
                  isClearable
                />
              </div>
            </div>
          </div>
        </div>
        <div className="timesheet__form">
        </div>

        <div className="timesheet__calendar">
          <TimeSheetCalendarComponent
            handleOpenModal={handleOpenModal}
            handleOpenModalId={handleOpenModalId}
            handleCloseToggle={handleCloseToggle}
            timeSheetData={timesheetListData}
            month={currentMonth}
            onMonthChange={handleMonthChange}
          />
        </div>

        <div className="csvExportDiv">
          <span className="csvExportTitle">CSVエクスポート</span>
          <div className="tsDatePicker">

            <div className="dateContainer">

              <div className="label">作成日時</div>
              <div className="dateSubContainer">
                <input type="date" className="inputDate" value={cvsStartDate} onChange={(e) => setCvsStartDate(e.target.value)} />
                <span>から</span>
              </div>

              <div className="dateSubContainer">
                <input type="date" className="inputDate" value={cvsEndDate} onChange={(e) => setCvsEndDate(e.target.value)} />
                <span>まで</span>
              </div>
            </div>

            <div>
              <AppExportCsv 
                type={"timesheets"} 
                startDate={cvsStartDate} 
                endDate={cvsEndDate}
                userId={userInputValue?.value ? parseInt(userInputValue.value) : undefined}
                groupId={groupInputValue?.value ? parseInt(groupInputValue.value) : undefined}
                status={statusInputValue?.value ? statusInputValue.value : undefined}
                title={spanInputValue?.label ? spanInputValue.label : undefined} // not used, "span" is better
                span={spanInputValue?.value ?? null}
              />
            </div>
          </div>
        </div>
      </div>
    </StyleTimeSheet>
  );
};
